import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import FileUploader from "../../../Components/FileUploader";
import useAuthStore from "../../../store/useAuthStore";
import { BHUTANESE_BANKS, PAYMENT_METHODS } from "../../../constants/banks";

const REQUIRED_CSV_HEADERS = ["Date", "Header", "Subheader", "Amount"];
const VALID_PM = PAYMENT_METHODS.map((p) => p.name);
const BANK_NAMES = BHUTANESE_BANKS.map((b) => b.name);

const FIELDS = [
  { key: "Date",             type: "date",       required: true  },
  { key: "Header",           type: "acctType",   required: true  },
  { key: "Subheader",        type: "acctSub",    required: true  },
  { key: "Amount",           type: "number",     required: true  },
  { key: "Description",      type: "text",       required: false },
  { key: "Bank Name",        type: "bankSelect", required: false },
  { key: "Payment Method",   type: "pmSelect",   required: false },
];

function validateRow(row, accounts) {
  const errors = {};

  if (!row.Date) errors.Date = "Required";

  const headerVal = (row.Header || "").trim();
  const validTypes = [...new Set(accounts.map((a) => a.type))];
  // Case-insensitive match so "direct expense" matches "Direct Expense"
  const matchedType = validTypes.find(
    (t) => t.toLowerCase() === headerVal.toLowerCase()
  );

  if (!headerVal) {
    errors.Header = "Required";
  } else if (accounts.length > 0 && !matchedType) {
    errors.Header = `"${headerVal}" not in COA. Valid types: ${validTypes.join(" · ")}`;
  }

  const subVal = (row.Subheader || "").trim();
  if (!subVal) {
    errors.Subheader = "Required";
  } else {
    // Use the case-insensitive matched type for filtering
    const resolvedType = matchedType || headerVal;
    const typed = accounts.filter(
      (a) => a.type.toLowerCase() === resolvedType.toLowerCase()
    );
    if (typed.length > 0) {
      const found = typed.some(
        (a) =>
          `${a.code}-${a.name}`.toLowerCase() === subVal.toLowerCase() ||
          a.name.toLowerCase() === subVal.toLowerCase() ||
          String(a.code) === subVal
      );
      if (!found) {
        const opts = typed.map((a) => `${a.code}-${a.name}`).join(" · ");
        errors.Subheader = `"${subVal}" not found under "${resolvedType}". Valid: ${opts}`;
      }
    }
  }

  const amt = parseFloat(String(row.Amount || "").replace(/,/g, ""));
  if (!row.Amount || isNaN(amt) || amt <= 0) errors.Amount = "Must be a positive number";

  if (row["Payment Method"] && !VALID_PM.includes(row["Payment Method"])) {
    errors["Payment Method"] = `Must be one of: ${VALID_PM.join(", ")}`;
  }

  return errors;
}

function BulkImportExpense() {
  const [rows, setRows]                   = useState([]);
  const [rowErrors, setRowErrors]         = useState({});
  const [activeTab, setActiveTab]         = useState("upload");
  const [file, setFile]                   = useState(null);
  const [fileError, setFileError]         = useState("");
  const [accounts, setAccounts]           = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState("");
  const [editingIdx, setEditingIdx]       = useState(null);
  const [editDraft, setEditDraft]         = useState({});
  const [draftErrors, setDraftErrors]     = useState({});
  const [importing, setImporting]         = useState(false);
  const [importResult, setImportResult]   = useState(null);
  const [filter, setFilter]               = useState("all");
  const [coaOpen, setCoaOpen]             = useState(false);

  const navigate = useNavigate();
  const { token } = useAuthStore();

  const accountTypes = useMemo(
    () => [...new Set(accounts.map((a) => a.type))].sort(),
    [accounts]
  );
  const accountsByType = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      if (!map[a.type]) map[a.type] = [];
      map[a.type].push(a);
    });
    return map;
  }, [accounts]);

  // Recompute errors whenever rows or accounts change
  useEffect(() => {
    if (rows.length === 0) return;
    const errs = {};
    rows.forEach((row, idx) => {
      const e = validateRow(row, accounts);
      if (Object.keys(e).length > 0) errs[idx] = e;
    });
    setRowErrors(errs);
  }, [rows, accounts]);

  // Fetch accounts when switching to validate tab
  useEffect(() => {
    if (activeTab === "validate" && accounts.length === 0 && !accountsLoading) {
      fetchAccounts();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    setAccountsError("");
    try {
      const res = await fetch("/api/getAllAccounts", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setAccountsError(data.error || "Failed to load Charts of Accounts.");
        return;
      }
      setAccounts(data.accounts || []);
    } catch {
      setAccountsError("Network error loading Charts of Accounts. Please retry.");
    } finally {
      setAccountsLoading(false);
    }
  };

  const readFile = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          // cellDates:true → date cells become JS Date objects (not serial numbers)
          // raw:true       → numeric cells come as actual JS numbers (not comma-formatted strings)
          const wb = XLSX.read(data, { type: "array", cellDates: true });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rawRows = XLSX.utils.sheet_to_json(ws, { raw: true, defval: "" });
          // Normalise every cell: Date → "YYYY-MM-DD", everything else → string
          const rows = rawRows.map((row) => {
            const out = {};
            for (const [k, v] of Object.entries(row)) {
              if (v instanceof Date) {
                const y = v.getFullYear();
                const m = String(v.getMonth() + 1).padStart(2, "0");
                const d = String(v.getDate()).padStart(2, "0");
                out[k] = `${y}-${m}-${d}`;
              } else {
                // Numbers come as-is via String() → no comma formatting
                out[k] = v === "" ? "" : String(v);
              }
            }
            return out;
          });
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(f);
    });

  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    setFileError("");
    setRows([]);
    setImportResult(null);
    setEditingIdx(null);
    if (!selectedFile) return;
    try {
      const data = await readFile(selectedFile);
      if (data.length === 0) { setFileError("File is empty."); return; }
      const headers = Object.keys(data[0]).map((h) => h.trim());
      const missing = REQUIRED_CSV_HEADERS.filter(
        (h) => !headers.some((fh) => fh.toLowerCase() === h.toLowerCase())
      );
      if (missing.length > 0) {
        setFileError(`Missing required columns: ${missing.join(", ")}`);
        return;
      }
      setRows(data);
    } catch {
      setFileError("Error reading file. Please check the format and try again.");
    }
  };

  const handleNext = () => {
    if (!file || rows.length === 0 || fileError) return;
    setActiveTab("validate");
  };

  // --- Row editing ---
  const handleEditRow = (idx) => {
    setEditingIdx(idx);
    const draft = { ...rows[idx] };
    setEditDraft(draft);
    setDraftErrors(validateRow(draft, accounts));
  };

  const handleDraftChange = (field, value) => {
    const updated = { ...editDraft, [field]: value };
    if (field === "Header") updated.Subheader = "";
    setEditDraft(updated);
    setDraftErrors(validateRow(updated, accounts));
  };

  const handleSaveRow = () => {
    const newRows = [...rows];
    newRows[editingIdx] = { ...editDraft };
    setRows(newRows);
    setEditingIdx(null);
    setEditDraft({});
    setDraftErrors({});
  };

  const handleCancelEdit = () => {
    setEditingIdx(null);
    setEditDraft({});
    setDraftErrors({});
  };

  const handleDeleteRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) { setEditingIdx(null); setEditDraft({}); }
    else if (editingIdx !== null && editingIdx > idx) setEditingIdx(editingIdx - 1);
  };

  // --- Derived ---
  const validRows = useMemo(
    () => rows.filter((_, idx) => !rowErrors[idx]),
    [rows, rowErrors]
  );
  const errorCount = rows.length - validRows.length;

  const filteredIndexes = useMemo(
    () =>
      rows.map((_, idx) => idx).filter((idx) => {
        if (filter === "valid")  return !rowErrors[idx];
        if (filter === "errors") return !!rowErrors[idx];
        return true;
      }),
    [rows, rowErrors, filter]
  );

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setImporting(true);
    try {
      const res = await fetch("/api/bulkImportExpense", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rows: validRows }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Import failed");
      setImportResult(result);
      if (result.imported > 0) setTimeout(() => navigate("/expense"), 3000);
    } catch (err) {
      setImportResult({ error: err.message });
    } finally {
      setImporting(false);
    }
  };

  // --- Cell rendering ---
  const renderCell = (row, idx, field, fieldDef) => {
    const isEditing = editingIdx === idx;
    const value = isEditing ? (editDraft[field] ?? "") : (row[field] ?? "");
    const err = isEditing ? draftErrors[field] : rowErrors[idx]?.[field];
    const cellClass = err ? "bg-red-50" : "";

    if (!isEditing) {
      return (
        <td key={field} className={`px-3 py-2 text-sm align-top ${cellClass}`}>
          <span className={err ? "text-red-700" : "text-gray-700"}>{value?.toString() || "—"}</span>
          {err && <p className="text-xs text-red-500 mt-0.5 whitespace-normal max-w-[160px]">{err}</p>}
        </td>
      );
    }

    // Edit mode
    const inputClass = `w-full text-sm border rounded px-2 py-1 focus:outline-none focus:ring-1 ${
      err ? "border-red-400 focus:ring-red-400" : "border-gray-300 focus:ring-blue-500"
    }`;

    let input;
    if (fieldDef.type === "acctType") {
      input = (
        <select value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass}>
          <option value="">— Select Type —</option>
          {accountTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      );
    } else if (fieldDef.type === "acctSub") {
      const opts = accountsByType[editDraft.Header] || [];
      input = (
        <select
          value={value}
          onChange={(e) => handleDraftChange(field, e.target.value)}
          disabled={!editDraft.Header}
          className={inputClass}
        >
          <option value="">— Select Account —</option>
          {opts.map((a) => (
            <option key={a._id} value={`${a.code}-${a.name}`}>
              {a.code} — {a.name}
            </option>
          ))}
        </select>
      );
    } else if (fieldDef.type === "pmSelect") {
      input = (
        <select value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass}>
          <option value="">— None —</option>
          {VALID_PM.map((pm) => <option key={pm} value={pm}>{pm}</option>)}
        </select>
      );
    } else if (fieldDef.type === "bankSelect") {
      input = (
        <select value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass}>
          <option value="">— None —</option>
          {BANK_NAMES.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      );
    } else if (fieldDef.type === "date") {
      input = (
        <input type="date" value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass} />
      );
    } else if (fieldDef.type === "number") {
      input = (
        <input type="number" value={value} min="0.01" step="0.01" onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass} />
      );
    } else {
      input = (
        <input type="text" value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass} />
      );
    }

    return (
      <td key={field} className="px-3 py-2 align-top">
        <div className="min-w-[130px]">
          {input}
          {err && <p className="text-xs text-red-500 mt-0.5">{err}</p>}
        </div>
      </td>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between px-4 mb-4 bg-white shadow rounded-lg h-[70px]">
        <span className="text-gray-700 font-semibold text-lg">Bulk Import Expenses</span>
        <button onClick={() => navigate("/expense")} className="text-blue-500 hover:underline text-sm">
          ← Back to Expenses
        </button>
      </div>

      <div className="mx-auto p-6 bg-white rounded-lg shadow-sm">
        {/* Tabs */}
        <div className="flex border-b mb-6">
          {[
            { id: "upload",   label: "1 · Upload" },
            { id: "validate", label: "2 · Validate & Edit" },
          ].map((tab) => {
            const locked = tab.id === "validate" && (!file || !!fileError || rows.length === 0);
            return (
              <button
                key={tab.id}
                onClick={() => (tab.id === "validate" ? handleNext() : setActiveTab("upload"))}
                disabled={locked}
                className={`px-5 py-2.5 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : locked
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Upload Tab ── */}
        {activeTab === "upload" && (
          <div>
            {fileError && (
              <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded">
                {fileError}
              </div>
            )}

            <h2 className="text-base font-medium text-gray-800 mb-4">Upload your expenses file</h2>

            <FileUploader
              onFileSelect={handleFileSelect}
              acceptedFormats={["csv", "tsv", "xls", "xlsx"]}
              maxSizeMB={25}
            />

            <p className="text-sm text-gray-500 mt-3">
              Download a{" "}
              <a href="/samples/expense_import_template.csv" className="text-blue-600 hover:underline" download>
                sample CSV template
              </a>{" "}
              to see the expected format.
            </p>

            <div className="bg-blue-50 p-4 rounded-lg mt-4 text-sm text-gray-700">
              <p className="font-medium text-gray-800 mb-1">Required columns</p>
              <p>
                <strong>Date</strong> · <strong>Header</strong> (account type from COA) ·{" "}
                <strong>Subheader</strong> (e.g. "5001-Office Supplies") · <strong>Amount</strong>
              </p>
              <p className="text-gray-500 mt-1">
                Optional: Description · Bank Name · Payment Method
              </p>
            </div>

            {file && rows.length > 0 && !fileError && (
              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {rows.length} rows parsed from <strong>{file.name}</strong>
                </span>
                <button
                  onClick={handleNext}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md font-medium"
                >
                  Next: Validate &amp; Edit →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Validate & Edit Tab ── */}
        {activeTab === "validate" && (
          <div>
            {/* Import result banner */}
            {importResult && (
              <div
                className={`mb-4 p-4 rounded-lg text-sm ${
                  importResult.error
                    ? "bg-red-50 border-l-4 border-red-500 text-red-700"
                    : importResult.imported > 0
                    ? "bg-green-50 border-l-4 border-green-500 text-green-700"
                    : "bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700"
                }`}
              >
                {importResult.error ? (
                  <p>{importResult.error}</p>
                ) : (
                  <>
                    <p className="font-semibold">Import Complete</p>
                    <p>Imported: {importResult.imported} · Failed: {importResult.failed}</p>
                    {importResult.errors?.length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-xs">
                        {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    )}
                    {importResult.imported > 0 && (
                      <p className="mt-1 text-xs">Redirecting to Expenses…</p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* COA loading / error / empty states */}
            {accountsLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading Charts of Accounts for validation…
              </div>
            )}
            {accountsError && (
              <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 text-red-700 text-sm flex items-center justify-between rounded">
                <span>{accountsError}</span>
                <button onClick={fetchAccounts} className="ml-4 underline text-blue-600 text-xs">
                  Retry
                </button>
              </div>
            )}
            {!accountsLoading && !accountsError && accounts.length === 0 && (
              <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-400 text-amber-800 text-sm rounded">
                No Charts of Accounts found. Header/Subheader validation is skipped.{" "}
                <a href="/ChartsOfAccount" className="underline text-blue-600">
                  Set up COA
                </a>{" "}
                first for full validation.
              </div>
            )}

            {/* COA Reference Panel */}
            {!accountsLoading && accounts.length > 0 && (
              <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setCoaOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 transition-colors"
                >
                  <span>
                    {coaOpen ? "▼" : "▶"}&nbsp; Valid Charts of Accounts ({accounts.length} accounts across {accountTypes.length} types)
                  </span>
                  <span className="text-xs text-gray-400 font-normal">{coaOpen ? "Click to collapse" : "Click to expand"}</span>
                </button>
                {coaOpen && (
                  <div className="px-4 py-3 bg-white grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {accountTypes.map((type) => (
                      <div key={type} className="border border-gray-100 rounded p-2.5">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1.5">{type}</p>
                        <ul className="space-y-0.5">
                          {(accountsByType[type] || []).map((a) => (
                            <li key={a._id} className="text-xs text-gray-600">
                              <span className="font-mono text-gray-500">{a.code}</span>
                              {" — "}
                              {a.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Summary + Filter bar */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-700">{rows.length} rows total</span>
                <span className="text-green-600 font-medium">✓ {validRows.length} valid</span>
                {errorCount > 0 && (
                  <span className="text-red-600 font-medium">✗ {errorCount} with errors</span>
                )}
              </div>
              <div className="flex gap-1.5">
                {[
                  { id: "all",    label: "All" },
                  { id: "valid",  label: "Valid" },
                  { id: "errors", label: "Errors" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      filter === f.id
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8">#</th>
                    {FIELDS.map((f) => (
                      <th key={f.key} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                        {f.key}
                        {f.required && <span className="text-red-400 ml-0.5">*</span>}
                      </th>
                    ))}
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-3 w-28" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredIndexes.length === 0 ? (
                    <tr>
                      <td colSpan={FIELDS.length + 3} className="text-center py-10 text-gray-400 text-sm">
                        No rows to display
                      </td>
                    </tr>
                  ) : (
                    filteredIndexes.map((idx) => {
                      const row = rows[idx];
                      const errs = rowErrors[idx] || {};
                      const isEditing = editingIdx === idx;
                      const hasError = Object.keys(errs).length > 0;

                      return (
                        <tr
                          key={idx}
                          className={
                            isEditing
                              ? "bg-blue-50"
                              : hasError
                              ? "bg-red-50/40"
                              : ""
                          }
                        >
                          <td className="px-3 py-2 text-xs text-gray-400 align-top">{idx + 1}</td>

                          {FIELDS.map((f) => renderCell(row, idx, f.key, f))}

                          <td className="px-3 py-2 align-top">
                            {!isEditing && (
                              hasError ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  Error
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  Valid
                                </span>
                              )
                            )}
                          </td>

                          <td className="px-3 py-2 align-top">
                            {isEditing ? (
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={handleSaveRow}
                                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 font-medium"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleEditRow(idx)}
                                  className="px-2 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteRow(idx)}
                                  className="px-2 py-1 border border-red-200 text-red-500 text-xs rounded hover:bg-red-50"
                                  title="Remove row"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer actions */}
            <div className="mt-5 flex items-center justify-between">
              <button
                onClick={() => setActiveTab("upload")}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to Upload
              </button>
              <button
                onClick={handleImport}
                disabled={importing || validRows.length === 0}
                className={`px-6 py-2 rounded-md text-white text-sm font-medium transition-colors ${
                  importing || validRows.length === 0
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {importing
                  ? "Importing…"
                  : `Import ${validRows.length} Valid Expense${validRows.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default BulkImportExpense;
